import React, { useState, ReactNode } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { AccordionDetails, AccordionSummary } from '@mui/material'
import { Material } from 'container/SalesOrder/interfaces'
import { isValidNumber } from 'commons/utils/string'
import { Alert, Icon } from 'components'
import { useSelector } from 'store/store'
import { TypesValidations, YesNoNotApplicable } from 'constants/enums'
import { formatToPTBRNumber, numberToDecimalString } from 'commons/utils/number'
import statusRascunho from 'constants/statusRascunho'

import * as S from './styles'
import { theme } from 'styles/theme/hav-theme'

export interface AccordionResumeProps {
  height?: number
  width?: string
  material: Material
  children: ReactNode
  errorStatus: string
  onClick?: () => void
}

const {
  ANSWERED,
  OUT_MINIMUM_ITEM,
  OUT_MINIMUM_ORDER,
  WITHOUT_QUOTA,
  OUT_OF_STOCK,
  INCOMPATIBLE_FACTORY,
  INCOMPLETE_DATA,
  INCONSISTENT_STATUS
} = statusRascunho

type Status = typeof statusRascunho[keyof typeof statusRascunho]

const getTitleError = (status: Status, minAmount?: number, minVal?: number) => {
  switch (status) {
    case OUT_MINIMUM_ITEM:
      return `Material não atendido: volume mínimo não atingido.
      ${
        minAmount
          ? `Adicione ao menos ${numberToDecimalString(
              minAmount
            )} pares por fábrica para prosseguir.`
          : ''
      }
      Se não editados, os materiais permanecerão em aberto.`
    case OUT_MINIMUM_ORDER:
      return `Material não atendido: valor mínimo não atingido.${
        minVal
          ? `Adicione ao menos R$${numberToDecimalString(minVal)} para prosseguir.`
          : ''
      } Se não editados, os materiais permanecerão em aberto.`
    case WITHOUT_QUOTA:
      return 'Material não atendido: a cota foi excedida.'
    case OUT_OF_STOCK:
      return 'Material não atendido: cor indisponível no estoque.'
    case INCOMPATIBLE_FACTORY:
      return 'Material não atendido: não há cota cadastrada na fábrica selecionada.'
    case INCOMPLETE_DATA:
      return 'Material não atendido: determine a fábrica/centro para prosseguir.'
    case INCONSISTENT_STATUS:
      return 'Inconsistência no status do material: a OV deve apresentar um único status - em linha ou fora de linha.'
    default:
      return 'Erro desconhecido. Consulte o backoffice para mais informações.'
  }
}

export const AccordionResume: React.FC<AccordionResumeProps> = ({
  height = 80,
  width = '100%',
  children,
  material,
  errorStatus,
  onClick
}) => {
  const [expanded, setExpanded] = useState<boolean>(material.stat !== ANSWERED)

  const { records } = useSelector((store) => store.salesOrder)

  const getItemCustomFlag = (itemNumber: number) => {
    const record = records.find((item) => item.nr_item_pedi_rasc === itemNumber)
    if (record) return record.fl_caix_grad === TypesValidations.YES
    return false
  }

  const handleAccordionChange =
    () => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded)
    }

  const getAlertKind = (status: string) => {
    const warningKinds = [WITHOUT_QUOTA, OUT_OF_STOCK]
    if (warningKinds.includes(status)) return 'warning'
    return 'error'
  }

  return (
    <S.AccordionStyled
      height={height}
      width={width}
      expanded={expanded}
      onChange={handleAccordionChange()}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} onClick={onClick}>
        <S.AccordionSummaryContent>
          {errorStatus === ANSWERED && (
            <S.AttendedMaterialIcon>
              <Icon icon="task-alt" color={theme.colors.alert.success.dark} size="18" />
            </S.AttendedMaterialIcon>
          )}
          <S.AccordionSummaryBox className="firstElement">
            <S.MaterialCode>
              <h1>
                Código do Material: <span>{material.cd_matr}</span>
              </h1>
              {getItemCustomFlag(material.nr_item_pedi_rasc) && (
                <S.VariableGridBoxTag>Caixa grade variável</S.VariableGridBoxTag>
              )}
            </S.MaterialCode>
            <h2>{material.ds_matr}</h2>
          </S.AccordionSummaryBox>
          <S.AccordionSummaryBoxWrapper>
            <S.AccordionSummaryBox>
              <h1>Custo Un.</h1>
              <h3>{formatToPTBRNumber(material.vl_item)}</h3>
            </S.AccordionSummaryBox>
            <S.AccordionSummaryBox>
              <h1>Custo Liq.</h1>
              <h3>{formatToPTBRNumber(material.vl_dsct)}</h3>
            </S.AccordionSummaryBox>
            <S.AccordionSummaryBox>
              <h1>Desc.</h1>
              <h3>{numberToDecimalString(material.pc_dsct)}%</h3>
            </S.AccordionSummaryBox>
            <S.AccordionSummaryBox>
              <h1>Total</h1>
              <h3>{formatToPTBRNumber(material.vl_tota)}</h3>
            </S.AccordionSummaryBox>
          </S.AccordionSummaryBoxWrapper>
        </S.AccordionSummaryContent>
        {errorStatus !== ANSWERED && (
          <div className="error">
            <Alert
              fullWidth
              kind={getAlertKind(errorStatus)}
              title={getTitleError(
                errorStatus,
                material.qt_mini_fatm,
                material.vl_mini_fatm
              )}
            />
          </div>
        )}
      </AccordionSummary>
      <AccordionDetails>
        {!!material.cota.dt_ano_mes && (
          <S.AccordionMainContent>
            <div>
              <h1>Cota excedida</h1>
              <h2>
                {material.cota.qt_cota_exce === 0
                  ? YesNoNotApplicable.NO_UPPERCASE
                  : material.cota.qt_cota_exce.toLocaleString()}
              </h2>
            </div>
            <div>
              <h1>Cota zona</h1>
              <h2>{material.cota.qt_cota_disp_zona.toLocaleString()}</h2>
            </div>
            <div>
              <h1>Cota GRV</h1>
              <h2>{material.cota.qt_cota_disp_grv.toLocaleString()}</h2>
            </div>
            <div>
              <h1>Cota GNV</h1>
              <h2>{material.cota.qt_cota_disp_gnv.toLocaleString()}</h2>
            </div>
            <div>
              <h1>Bloqueio</h1>
              <h2>{material.cota.tp_bloq_pivo}</h2>
            </div>
            <div>
              <h1>Cota Livre</h1>
              <h2>
                {isValidNumber(material.cota.fl_pivo_lvre)
                  ? Number(material.cota.fl_pivo_lvre).toLocaleString()
                  : material.cota.fl_pivo_lvre}
              </h2>
            </div>
            <div>
              <h1>Estoque Livre</h1>
              <h2>
                {isValidNumber(material.cota.fl_estq_lvre)
                  ? Number(material.cota.fl_estq_lvre).toLocaleString()
                  : material.cota.fl_estq_lvre}
              </h2>
            </div>
            <div>
              <h1>Fábrica/Centro</h1>
              <h2>{material.cota.cd_cent_matr}</h2>
            </div>
            <div>
              <h1>Mês Cota</h1>
              <h2>{material.cota.ds_ano_mes}</h2>
            </div>
          </S.AccordionMainContent>
        )}
        <div>{children}</div>
      </AccordionDetails>
    </S.AccordionStyled>
  )
}
